import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChannelBadge, CHANNEL_COLORS } from "@/components/badges/ChannelBadge";
import { TemperaturePill } from "@/components/badges/TemperaturePill";
import { formatRelative, formatIdr, parseBackendDate } from "@/lib/format";
import {
  Inbox, Calendar, Bell, Users, Activity, Building2, Flame,
  UserCog, MessageSquare,
} from "lucide-react";
import type { Channel, LeadTemperature } from "@ai-leasing-os/shared";

// ---------------------------------------------------------------------------
// Types - minimal slices we read from the APIs we already have
// ---------------------------------------------------------------------------

interface ConversationRow {
  id: string;
  contact_id: string;
  channel: Channel;
  status: string;
  human_takeover: number;
  display_name: string | null;
  score: number;
  temperature: LeadTemperature;
  last_message_at: string | null;
}

interface ContactRow {
  id: string;
  display_name: string | null;
  status: string;
  score: number;
  temperature: LeadTemperature;
  primary_phone: string | null;
  updated_at: string;
}

interface VisitRow {
  id: string;
  contact_id: string;
  property_id: string | null;
  status: string;
  scheduled_start: string;
  visit_type: string;
  contact_display_name?: string | null;
  property_name?: string | null;
}

interface NotificationRow {
  id: string;
  is_read: number;
}

interface ActivityRow {
  id: string;
  actor_type: string;
  event_type: string;
  contact_id: string | null;
  conversation_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface PortfolioSummary {
  counts: { total: number; active: number; available_now: number };
  by_stay: { long_term: number; medium_term: number; short_term: number; unspecified: number };
}

// ---------------------------------------------------------------------------

export function DashboardPage() {
  const conversationsQ = useQuery({
    queryKey: ["dashboard", "conversations"],
    queryFn: () => api.get<ConversationRow[]>("/api/conversations?limit=500"),
    refetchInterval: 30_000,
  });
  const contactsQ = useQuery({
    queryKey: ["dashboard", "contacts"],
    queryFn: () => api.get<ContactRow[]>("/api/contacts?limit=500"),
    refetchInterval: 30_000,
  });
  const visitsQ = useQuery({
    queryKey: ["dashboard", "visits"],
    queryFn: () => api.get<VisitRow[]>("/api/visits?limit=200"),
    refetchInterval: 30_000,
  });
  const notifsQ = useQuery({
    queryKey: ["dashboard", "notifications"],
    queryFn: () => api.get<NotificationRow[]>("/api/notifications?unread=true&limit=200"),
    refetchInterval: 30_000,
  });
  const portfolioQ = useQuery({
    queryKey: ["dashboard", "portfolio"],
    queryFn: () => api.get<PortfolioSummary>("/api/business/portfolio-summary"),
    refetchInterval: 60_000,
  });
  const activityQ = useQuery({
    queryKey: ["dashboard", "activity"],
    queryFn: () => api.get<ActivityRow[]>("/api/activity?limit=12"),
    refetchInterval: 20_000,
  });

  const conversations = conversationsQ.data ?? [];
  const contacts = contactsQ.data ?? [];
  const visits = visitsQ.data ?? [];
  const unreadNotifs = notifsQ.data ?? [];
  const activity = activityQ.data ?? [];

  // First-paint guard: while a query is still on its initial fetch we have no
  // data yet, so the widgets below must show "Loading…" instead of their
  // genuine-empty copy ("No contacts yet"). Without this the dashboard flashes
  // a misleading empty state on every open.
  const contactsLoading = contactsQ.isLoading;
  const visitsLoading = visitsQ.isLoading;
  const activityLoading = activityQ.isLoading;

  // ---- Derived numbers ---------------------------------------------------
  const openConvs = conversations.filter((c) => c.status === "open").length;
  const takeoverConvs = conversations.filter((c) => c.human_takeover === 1).length;
  const hotContacts = contacts.filter((c) => c.score >= 60).length;
  const veryHot = contacts.filter((c) => c.temperature === "very_hot" || c.temperature === "hot").length;

  const now = Date.now();
  const in7d = now + 7 * 24 * 60 * 60 * 1000;
  const upcomingVisits = visits
    .filter((v) => v.status === "scheduled")
    .filter((v) => {
      const t = parseBackendDate(v.scheduled_start).getTime();
      return t >= now && t <= in7d;
    })
    .sort(
      (a, b) =>
        parseBackendDate(a.scheduled_start).getTime() -
        parseBackendDate(b.scheduled_start).getTime(),
    );

  const topHot = [...contacts]
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  // Conversations grouped by channel
  const byChannel: Record<Channel, number> = {
    whatsapp: 0, instagram: 0, messenger: 0, marketplace: 0,
  };
  for (const c of conversations) byChannel[c.channel]++;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">
          Live snapshot - refreshes every 30s.
        </p>
      </header>

      {/* ----- KPI row ----- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          icon={<Inbox className="h-4 w-4" />}
          label="Open conversations"
          value={openConvs}
          sub={takeoverConvs > 0 ? `${takeoverConvs} in human takeover` : "no human takeover"}
          tone={takeoverConvs > 0 ? "warn" : "default"}
          to="/inbox"
        />
        <Kpi
          icon={<Flame className="h-4 w-4" />}
          label="Hot contacts (≥60)"
          value={hotContacts}
          sub={`${veryHot} hot/very hot · ${contacts.length} total`}
          tone={hotContacts > 0 ? "accent" : "default"}
          to="/contacts"
        />
        <Kpi
          icon={<Calendar className="h-4 w-4" />}
          label="Visits next 7 days"
          value={upcomingVisits.length}
          sub={`${visits.length} all-time`}
          to="/visits"
        />
        <Kpi
          icon={<Bell className="h-4 w-4" />}
          label="Unread notifications"
          value={unreadNotifs.length}
          sub="dashboard alerts"
          to="/notifications"
          tone={unreadNotifs.length > 0 ? "warn" : "default"}
        />
      </div>

      {/* ----- Top widgets ----- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hot leads list */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-4 w-4" />
                Top 5 hot contacts
              </CardTitle>
              <Link to="/contacts" className="text-xs text-slate-500 hover:text-slate-900 hover:underline">
                See all →
              </Link>
            </div>
            <CardDescription>
              Highest scores. Click a name to open the contact.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 py-0">
            {contactsLoading ? (
              <SkeletonRows />
            ) : topHot.length === 0 ? (
              <EmptyState text="No contacts yet." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {topHot.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/contacts/${c.id}`}
                      className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      <span
                        className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0"
                      >
                        {(c.display_name ?? "?").charAt(0).toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {c.display_name ?? "(no name)"}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {c.primary_phone ?? "-"} · {c.status}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <TemperaturePill temperature={c.temperature} />
                        <span className="text-sm font-semibold tabular-nums text-slate-900 w-8 text-right">
                          {c.score}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Upcoming visits */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Visits - next 7 days
              </CardTitle>
              <Link to="/visits" className="text-xs text-slate-500 hover:text-slate-900 hover:underline">
                See all →
              </Link>
            </div>
            <CardDescription>
              Scheduled only. Confirmed + no-shows are filtered out here.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0 py-0">
            {visitsLoading ? (
              <SkeletonRows />
            ) : upcomingVisits.length === 0 ? (
              <EmptyState text="Nothing scheduled in the next 7 days." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcomingVisits.slice(0, 5).map((v) => (
                  <li key={v.id} className="px-5 py-2.5 flex items-center gap-3">
                    <div className="h-9 w-9 rounded bg-slate-100 flex flex-col items-center justify-center shrink-0">
                      <span className="text-[9px] uppercase tracking-wider text-slate-500">
                        {parseBackendDate(v.scheduled_start).toLocaleDateString("en-GB", { month: "short" })}
                      </span>
                      <span className="text-sm font-semibold text-slate-900 leading-none">
                        {parseBackendDate(v.scheduled_start).getDate()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {v.contact_display_name ?? "(unnamed contact)"}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {v.property_name ?? "(no property)"} · {v.visit_type}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 shrink-0 tabular-nums">
                      {parseBackendDate(v.scheduled_start).toLocaleTimeString("en-GB", {
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ----- Channels + Portfolio ----- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Conversations by channel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Conversations by channel
            </CardTitle>
            <CardDescription>Distribution across your transport layers.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {(Object.keys(byChannel) as Channel[]).map((ch) => {
              const n = byChannel[ch];
              const max = Math.max(1, ...Object.values(byChannel));
              const pct = Math.round((n / max) * 100);
              return (
                <div key={ch} className="flex items-center gap-2">
                  <div className="flex items-center gap-2 w-32 shrink-0">
                    <ChannelBadge channel={ch} />
                    <span className="text-xs text-slate-700">{CHANNEL_COLORS[ch].label}</span>
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: CHANNEL_COLORS[ch].hex }}
                    />
                  </div>
                  <span className="w-8 text-right tabular-nums text-sm font-medium text-slate-900">{n}</span>
                </div>
              );
            })}
            <div className="text-[10px] text-slate-400 pt-1">
              {conversations.length} total conversations across all channels
            </div>
          </CardContent>
        </Card>

        {/* Properties portfolio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Your portfolio
            </CardTitle>
            <CardDescription>Computed live from Properties.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!portfolioQ.data ? (
              <EmptyState text="Loading…" />
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <Mini label="Total active" value={portfolioQ.data.counts.active} />
                  <Mini label="Available now" value={portfolioQ.data.counts.available_now} accent />
                  <Mini label="All time" value={portfolioQ.data.counts.total} muted />
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <StayCount label="Long-term" hint="≥ 6 months" value={portfolioQ.data.by_stay.long_term} />
                  <StayCount label="Medium" hint="1-5 months" value={portfolioQ.data.by_stay.medium_term} />
                  <StayCount label="Short-term" hint="< 1 month" value={portfolioQ.data.by_stay.short_term} />
                </div>
                <Link to="/properties" className="block text-center text-xs text-slate-500 hover:text-slate-900 hover:underline pt-2">
                  Open Properties →
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ----- Activity feed ----- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Recent activity
          </CardTitle>
          <CardDescription>Everything the system did in the last few hours.</CardDescription>
        </CardHeader>
        <CardContent className="px-0 py-0">
          {activityLoading ? (
            <SkeletonRows />
          ) : activity.length === 0 ? (
            <EmptyState text="No activity yet." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {activity.map((e) => (
                <li key={e.id} className="px-5 py-2 flex items-start gap-3">
                  <ActorIcon actor={e.actor_type} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">
                      <span className="font-medium">{prettyEvent(e.event_type)}</span>
                      {renderMetaHint(e)}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {e.actor_type} · {formatRelative(e.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------

function Kpi({
  icon, label, value, sub, tone = "default", to,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
  tone?: "default" | "warn" | "accent";
  to?: string;
}) {
  const valueClass =
    tone === "warn" ? "text-amber-600" :
    tone === "accent" ? "text-orange-600" :
    "text-slate-900";
  const card = (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between text-xs uppercase tracking-wider text-slate-500">
          <span>{label}</span>
          {icon}
        </div>
        <div className={`mt-2 text-3xl font-semibold tabular-nums ${valueClass}`}>
          {value}
        </div>
        <div className="text-xs text-slate-400">{sub}</div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

function Mini({ label, value, accent, muted }: { label: string; value: number; accent?: boolean; muted?: boolean }) {
  return (
    <div className={`rounded border p-2 ${accent ? "bg-green-50 border-green-200" : muted ? "bg-slate-50 border-slate-200" : "bg-brand-surface border-slate-200"}`}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${accent ? "text-green-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function StayCount({ label, hint, value }: { label: string; hint: string; value: number }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-center">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-900">{value}</div>
      <div className="text-[9px] text-slate-400">{hint}</div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="px-5 py-8 text-center text-xs text-slate-400">{text}</div>;
}

/**
 * Shimmer placeholder rows shown during a widget's initial fetch so the
 * dashboard doesn't flash a misleading "No data yet" empty state on open.
 */
function SkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <ul className="divide-y divide-slate-100">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-5 py-2.5">
          <span className="h-8 w-8 rounded-full bg-slate-100 animate-pulse shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-slate-100 animate-pulse" />
            <div className="h-2.5 w-1/4 rounded bg-slate-100 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ActorIcon({ actor }: { actor: string }) {
  const cls = "h-6 w-6 rounded-full flex items-center justify-center text-white shrink-0";
  if (actor === "ai") return <span className={`${cls} bg-slate-900`} title="AI"><MessageSquare className="h-3.5 w-3.5" /></span>;
  if (actor === "human") return <span className={`${cls} bg-amber-500`} title="Human"><UserCog className="h-3.5 w-3.5" /></span>;
  return <span className={`${cls} bg-slate-300`} title="System"><Activity className="h-3.5 w-3.5" /></span>;
}

function prettyEvent(t: string): string {
  return t.replace(/_/g, " ");
}

function renderMetaHint(e: ActivityRow): React.ReactNode {
  const m = e.metadata;
  if (!m || typeof m !== "object") return null;
  const hints: string[] = [];
  if (typeof (m as { channel?: unknown }).channel === "string") hints.push(`(${(m as { channel: string }).channel})`);
  if (typeof (m as { table?: unknown }).table === "string") hints.push(`(${(m as { table: string }).table})`);
  if (typeof (m as { property_id?: unknown }).property_id === "string") hints.push("property");
  if (typeof (m as { score_delta?: unknown }).score_delta === "number") {
    const d = (m as { score_delta: number }).score_delta;
    hints.push(`score ${d > 0 ? "+" : ""}${d}`);
  }
  if (hints.length === 0) return null;
  return <span className="text-[11px] text-slate-500 ml-1">{hints.join(" · ")}</span>;
}

// Avoid the "unused import" warning if we don't actually render formatIdr
// somewhere - keeping the import for future inline pricing snippets.
void formatIdr;
